import {matchPath} from "react-router-dom-v5-compat";

import {isGuideChatDisabled} from "@khan/guide-core";
import {
    isActivitiesPage,
    isParentDefaultActivityPage,
    isTeacherDefaultActivityPage,
} from "@khan/guide-core-helpers";
import {useUserAttributes} from "@khan/shared-core";
import {useLocation} from "@khan/urls";

import {HeaderType} from "./types.ts";

interface HeaderProps {
    /* Whether to show the Khanmigo label. */
    showKhanmigoLabel: boolean;
    /* Whether to show the teacher toggle. */
    showStudentToggle: boolean;
    /* Chat version of header (non-activities) has special styling. */
    isChatHeader: boolean;
}

/**
 * Determine if a path represents a Khanmigo teacher tool page
 */
const isTeacherToolPage = (pathname: string): boolean => {
    return !!matchPath(`/teacher/khanmigo-tools/:tool`, pathname);
};

/**
 * A custom hook to compute props required for the shared Khanmigo header.
 *
 * @param type The type of header being rendered.
 */
export const usePropsForHeaderType = (type: HeaderType): HeaderProps => {
    const {pathname} = useLocation();
    const attributes = useUserAttributes();

    const isTeacher = attributes?.hasAccessToAIGuideTeacher;
    const isParent = attributes?.hasAccessToAIGuideParent;
    const isLearner = attributes?.hasAccessToAIGuideLearner;

    const chatDisabled = isGuideChatDisabled(pathname);

    const showToggleForTeacher =
        isLearner && isTeacher
            ? isTeacherDefaultActivityPage(pathname)
                ? true
                : !chatDisabled &&
                  !isActivitiesPage(pathname) &&
                  !isTeacherToolPage(pathname)
            : false;

    const showToggleForParent =
        isLearner && isParent
            ? isParentDefaultActivityPage(pathname)
                ? true
                : false
            : false;

    const showStudentToggle = showToggleForTeacher || showToggleForParent;
    const showKhanmigoLabel = type !== HeaderType.SmallScreenActivity;
    const isChatHeader =
        HeaderType.DesktopChat === type || HeaderType.SmallScreenChat === type;

    return {
        showKhanmigoLabel,
        showStudentToggle,
        isChatHeader,
    };
};
